import { UserSwitchOutlined } from "@ant-design/icons";
import Typography from "antd/es/typography/Typography";
import { useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getTaskListsThunk, getWorkGetScheduleThunk } from "tm-shared/api";
import { AnyTask, Market, TaskAssignment, TaskRoute } from "tm-shared/types";

import Header from "@/components/Header";
import { MainContainer } from "@/components/Layout/Styled";
import { TasksModalsContainer } from "@/components/TasksModals/TasksModalsContainer";
import { HeaderNavLink } from "@/components/UI/HeaderNavLink";
import { HeaderReloadBtn } from "@/components/UI/HeaderReloadBtn";
import { MANAGER_AGENT_VOCABULARY, ROUTES } from "@/constants";
import { isManagerRole } from "@/hooks/isManagerRole";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import useAsyncAction from "@/hooks/useAsyncAction";
import { useGlobalCooldown } from "@/hooks/useGlobalCooldown";
import { useQueueInfo } from "@/hooks/useQueueInfo";
import { AssignmentFilter } from "@/pages/Assignments/AssignmentsPage/components/AssignmentFilter";
import { AssignmentMarketList } from "@/pages/Assignments/AssignmentsPage/components/AssignmentMarketList";

import { AssignmentWithContext } from "./AssignmentPage.types";
import { StyledMarketListWrapper } from "./Styled";

const isAssignment = (t: AnyTask): t is TaskAssignment =>
    t.context.route === TaskRoute.CustomTask;

const AssignmentsPage = () => {
    const currentLocation = useRef("");
    const { disabled, trigger } = useGlobalCooldown(30_000);
    const { tasks, markets } = useAppSelector((s) => s.work);
    const { offlineMode, isUnscheduledMarketsProcessing } = useAppSelector(
        (s) => s.offline,
    );
    const { id: userId, role } = useAppSelector((s) => s.user);
    const { activeSchedule } = useAppSelector((s) => s.sv);
    const dispatch = useAppDispatch();
    const { hasPending } = useQueueInfo();
    const location = useLocation();
    const isManager = isManagerRole(role);
    const [tasksInfoModal, setTasksInfoModal] = useState<{
        market: Market;
        task: AnyTask;
    } | null>(null);
    currentLocation.current = location.pathname?.split("/")[1];

    const { filter: currentFilter } = useAppSelector(
        (s) => s.ui.assignments.page,
    );

    const assignmentsWithContext: AssignmentWithContext[] = useMemo(() => {
        return Object.entries(tasks).flatMap(([marketId, tMap]) =>
            Object.values(tMap)
                .filter(isAssignment)
                .map((t) => ({
                    assignment: t,
                    market: markets[marketId],
                    allTasks: tasks[marketId],
                    id: t.id,
                })),
        );
    }, [markets, tasks]);

    const filteredAssignmentsWithContext = useMemo(() => {
        if (!currentFilter) return assignmentsWithContext;

        return assignmentsWithContext.filter(
            (item) =>
                item.assignment.context.additionalData.assignmentType ===
                currentFilter,
        );
    }, [assignmentsWithContext, currentFilter]);

    const [handleSchedulesReload, schedulesReloadLoading] = useAsyncAction(
        async () => {
            trigger();
            const currentMarketIds = Object.keys(markets);
            const roleSlug = isManager
                ? MANAGER_AGENT_VOCABULARY[role]
                : undefined;
            await dispatch(
                getWorkGetScheduleThunk({
                    userId: isManager ? activeSchedule : userId,
                    roleSlug,
                }),
            );
            await dispatch(
                getTaskListsThunk({ targetIds: currentMarketIds, roleSlug }),
            );
        },
    );

    const handleOpenTasksInfoModal = (modal: {
        market: Market;
        task: AnyTask;
    }) => {
        setTasksInfoModal(modal);
    };

    const handleCloseTasksInfoModal = () => {
        setTasksInfoModal(null);
    };

    return (
        <>
            <Header
                titleLoading={schedulesReloadLoading}
                rightActions={
                    <>
                        <HeaderNavLink to={ROUTES.AssignmentsPage}>
                            <UserSwitchOutlined />
                        </HeaderNavLink>

                        <HeaderReloadBtn
                            onClick={handleSchedulesReload}
                            disabled={
                                disabled ||
                                offlineMode ||
                                hasPending ||
                                isUnscheduledMarketsProcessing
                            }
                        />
                    </>
                }
            />

            <AssignmentFilter />
            <MainContainer>
                <StyledMarketListWrapper>
                    {filteredAssignmentsWithContext.length > 0 ? (
                        <AssignmentMarketList
                            assignmentsWithContext={
                                filteredAssignmentsWithContext
                            }
                            onOpenTasksInfoModal={handleOpenTasksInfoModal}
                        />
                    ) : (
                        <Typography>Доручень не знайдено</Typography>
                    )}
                </StyledMarketListWrapper>
            </MainContainer>

            <TasksModalsContainer
                modal={tasksInfoModal}
                onClose={handleCloseTasksInfoModal}
            />
        </>
    );
};

export default AssignmentsPage;
